import { useState } from "react";
import type { SagittalRom, JointKey } from "../../gait/rom";
import { REFERENCE_CURVES, JOINT_LABEL } from "../../gait/refCurves";
import { PHASES } from "../../gait/phases";

const W = 680;
const H = 250;
const PAD = { l: 36, r: 14, t: 22, b: 30 };
const PLOT_W = W - PAD.l - PAD.r;
const PLOT_H = H - PAD.t - PAD.b;

// Canonical phase end-fractions (toe-off ≈ 62%).
const PHASE_ENDS = [0.02, 0.12, 0.31, 0.5, 0.62, 0.75, 0.87, 1.0];

const JOINTS: JointKey[] = ["hip", "knee", "ankle"];

export function KinematicCurves({ rom }: { rom: SagittalRom }) {
  const [joint, setJoint] = useState<JointKey>("knee");
  const ref = REFERENCE_CURVES[joint];
  const L = rom.curves[joint].L;
  const R = rom.curves[joint].R;

  const all = [...ref.low, ...ref.high, ...L, ...R].filter((v) => isFinite(v));
  let yMin = Math.min(...all);
  let yMax = Math.max(...all);
  const padY = (yMax - yMin) * 0.12 || 5;
  yMin -= padY;
  yMax += padY;

  const x = (i: number) => PAD.l + (i / 100) * PLOT_W;
  const y = (v: number) => PAD.t + (1 - (v - yMin) / (yMax - yMin || 1)) * PLOT_H;

  const line = (s: number[]) =>
    s.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  const band =
    ref.high.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ") +
    " " +
    [...ref.low].reverse().map((v, i) => `L${x(100 - i).toFixed(1)},${y(v).toFixed(1)}`).join(" ") +
    " Z";

  const zeroY = yMin < 0 && yMax > 0 ? y(0) : null;

  return (
    <div className="card curve-card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <strong>Joint motion · estimated</strong>
        <span className="pill">{rom.confidence} confidence</span>
      </div>

      <div className="phase-tabs" style={{ marginTop: 12 }}>
        {JOINTS.map((j) => (
          <button key={j} className={j === joint ? "active" : ""} onClick={() => setJoint(j)}>
            {j[0].toUpperCase() + j.slice(1)}
          </button>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{ marginTop: 12 }}>
        <defs>
          <clipPath id="sweep">
            <rect x={PAD.l} y={0} width={0} height={H}>
              <animate attributeName="width" from="0" to={PLOT_W} dur="0.9s" fill="freeze" calcMode="spline" keySplines="0.2 0.8 0.2 1" />
            </rect>
          </clipPath>
          <linearGradient id="lgrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7cc4ff" />
            <stop offset="100%" stopColor="#5eead4" />
          </linearGradient>
        </defs>

        {/* phase guide lines + labels */}
        {PHASE_ENDS.map((f, i) => {
          const px = PAD.l + f * PLOT_W;
          const startF = i === 0 ? 0 : PHASE_ENDS[i - 1];
          const midX = PAD.l + ((startF + f) / 2) * PLOT_W;
          const isToeOff = Math.abs(f - 0.62) < 1e-6;
          return (
            <g key={i}>
              <line
                x1={px}
                y1={PAD.t}
                x2={px}
                y2={PAD.t + PLOT_H}
                stroke={isToeOff ? "rgba(255,159,10,0.5)" : "rgba(255,255,255,0.08)"}
                strokeWidth={isToeOff ? 1.4 : 1}
                strokeDasharray={isToeOff ? "4 3" : undefined}
              />
              <text x={midX} y={PAD.t - 8} textAnchor="middle" fontSize="9" fill="rgba(245,245,247,0.45)">
                {PHASES[i]}
              </text>
            </g>
          );
        })}

        {/* zero line */}
        {zeroY !== null && (
          <line x1={PAD.l} y1={zeroY} x2={PAD.l + PLOT_W} y2={zeroY} stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
        )}

        {/* axes labels */}
        <text x={PAD.l} y={H - 8} fontSize="9" fill="rgba(245,245,247,0.4)">0%</text>
        <text x={PAD.l + PLOT_W} y={H - 8} fontSize="9" fill="rgba(245,245,247,0.4)" textAnchor="end">100% cycle</text>
        <text x={10} y={PAD.t + 6} fontSize="9" fill="rgba(245,245,247,0.4)">{Math.round(yMax)}°</text>
        <text x={10} y={PAD.t + PLOT_H} fontSize="9" fill="rgba(245,245,247,0.4)">{Math.round(yMin)}°</text>

        <g clipPath="url(#sweep)">
          {/* reference corridor */}
          <path d={band} fill="rgba(255,255,255,0.07)" stroke="none" />
          <path
            d={line(ref.mean)}
            fill="none"
            stroke="rgba(255,255,255,0.35)"
            strokeWidth="1.5"
            strokeDasharray="5 4"
          />
          {/* patient curves */}
          <path d={line(L)} fill="none" stroke="#7cc4ff" strokeWidth="2.6" strokeLinejoin="round" />
          <path d={line(R)} fill="none" stroke="#5eead4" strokeWidth="2.6" strokeLinejoin="round" />
        </g>
      </svg>

      <div style={{ fontSize: 13, color: "var(--dim)", marginTop: 2 }}>{JOINT_LABEL[joint]}</div>
      <div className="legend">
        <span><i style={{ background: "#7cc4ff" }} /> Left</span>
        <span><i style={{ background: "#5eead4" }} /> Right</span>
        <span><i style={{ background: "rgba(255,255,255,0.5)" }} /> Typical pattern</span>
        <span style={{ color: "var(--warn)" }}>┊ toe-off</span>
      </div>
    </div>
  );
}
